import React, { useRef } from 'react';
import { lonLatToXY, svgPathForRings, xyToLonLat } from '../mapProjection.mjs';
import { nightRing } from './terminator.mjs';
import { formatCoords, localSolarTime } from '../format.mjs';

const WIDTH = 1000;
const HEIGHT = 500;

/*
 * A small inset map pinned to the corner of the tutorial stage, showing where
 * on Earth the lesson is standing you.
 *
 * The sky above is easy to lose your bearings in, and every lesson is really a
 * claim about a place: Polaris climbs *because* you are sailing north, and the
 * three skies in the last lesson differ *because* the observer jumped a
 * continent. Without something showing the ground, all of that reads as the
 * stars moving on their own.
 *
 * It is driven straight off the same lesson state that drives the sky, so the
 * pin and the shadow move on exactly the frames the stars do.
 */
export default function TutorialMap({ rings, lat, lon, date, name, onPick }) {
  const svgRef = useRef(null);
  const dragging = useRef(false);
  const here = lonLatToXY(lon, lat, WIDTH, HEIGHT);
  const night = svgPathForRings([nightRing(date)], WIDTH, HEIGHT);

  /*
   * Picking is by pointer position rather than by click alone, so the observer
   * can be dragged around the world and the sky follows continuously. That is
   * the point of the explorer: sliding up a meridian and watching the pole climb
   * is the latitude lesson done with your own hand.
   */
  const pick = (event) => {
    if (!onPick || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * WIDTH;
    const y = ((event.clientY - rect.top) / rect.height) * HEIGHT;
    const at = xyToLonLat(x, y, WIDTH, HEIGHT);
    onPick({ lat: Math.max(-85, Math.min(85, at.lat)), lon: at.lon });
  };

  const handlers = onPick
    ? {
        onPointerDown: (event) => {
          dragging.current = true;
          event.currentTarget.setPointerCapture(event.pointerId);
          pick(event);
        },
        onPointerMove: (event) => {
          if (dragging.current) pick(event);
        },
        onPointerUp: (event) => {
          dragging.current = false;
          event.currentTarget.releasePointerCapture(event.pointerId);
        },
        onPointerCancel: () => {
          dragging.current = false;
        }
      }
    : {};

  return (
    <figure className={`sg-minimap${onPick ? ' is-pickable' : ''}`}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role={onPick ? 'button' : 'img'}
        aria-label={
          onPick ? 'World map. Drag to move the observer.' : `Observer at ${formatCoords(lat, lon)}`
        }
        {...handlers}
      >
        <rect width={WIDTH} height={HEIGHT} className="sg-minimap-sea" />
        <path d={svgPathForRings(rings, WIDTH, HEIGHT)} className="sg-minimap-land" fillRule="evenodd" />
        <path d={night} className="sg-minimap-night" />

        {/* The observer's parallel: lesson three is entirely about how high this line sits. */}
        <line x1="0" y1={here.y} x2={WIDTH} y2={here.y} className="sg-minimap-parallel" />

        <g className="sg-minimap-pin" transform={`translate(${here.x} ${here.y})`}>
          <circle r="26" className="sg-minimap-halo" />
          <circle r="9" />
        </g>
      </svg>
      {/*
        The clock is local solar time, not a timezone: it is what the sky overhead
        actually says the hour is. Paired with the shadow it makes the point the
        last lesson is built on, that one instant is a different hour of the night
        at every longitude.
      */}
      <figcaption>
        <span>{name ? `${name} · ${formatCoords(lat, lon)}` : formatCoords(lat, lon)}</span>
        <span className="sg-minimap-clock">{localSolarTime(date, lon)} local</span>
      </figcaption>
    </figure>
  );
}
